import DoneIcon from '@mui/icons-material/Done'
import ErrorTwoToneIcon from '@mui/icons-material/ErrorTwoTone'
import {
	CircularProgress,
	ListItem,
	ListItemIcon,
	ListItemText,
	Tooltip,
} from '@mui/material'
import * as React from 'react'
import { useTasks } from './store'

export interface TaskItemProps {
	id: string
	label: React.ReactNode
	icon: React.ReactElement
	onInit: () => void
}

export function TaskItem(props: TaskItemProps) {
	const didInit = React.useRef(false)
	const { task, updaters } = useTasks((state) => ({
		task: state.tasks[props.id],
		updaters: state.getUpdaters(props.id),
	}))

	React.useEffect(
		function init() {
			if (didInit.current || !task || task.status !== 'idle') {
				return
			}

			didInit.current = true

			try {
				updaters.updateStatus('progress')
				Promise.resolve(props.onInit()).catch((e) =>
					updaters.updateStatus('error', e?.message ?? ''),
				)
			} catch (e: any) {
				updaters.updateStatus('error', e.message)
			}
		},
		[props, task, updaters],
	)

	return (
		<ListItem
			secondaryAction={<SecondaryAction task={task} />}
			sx={{ maxWidth: 400 }}
		>
			{props.icon && <ListItemIcon>{props.icon}</ListItemIcon>}
			<ListItemText
				color={task.status === 'error' ? 'error' : undefined}
				primary={props.label}
				secondary={
					task.status === 'error'
						? task.metadata?.errors?.length === 1
							? task.metadata.errors[0]
							: 'A problem occurred'
						: null
				}
			/>
		</ListItem>
	)
}

function SecondaryAction({ task }) {
	if (task.status === 'progress') {
		return (
			<CircularProgress
				value={task.progress}
				variant={task.progress ? 'determinate' : 'indeterminate'}
				size={14}
			/>
		)
	}

	if (task.status === 'error') {
		return (
			<Tooltip
				placement="left-start"
				arrow
				disableHoverListener={task.metadata.errors.length === 1}
				title={
					<ul>
						{task.metadata.errors.map((err) => (
							<li key={err}>{err}</li>
						))}
					</ul>
				}
			>
				<ErrorTwoToneIcon color="error" />
			</Tooltip>
		)
	}

	if (task.status === 'success') {
		return <DoneIcon />
	}

	return null
}
